import {
  isEmptyArray,
  isNil,
  isNonEmptyArray,
  isNonEmptyString,
  isNumber,
  isObject,
  isString,
  isUndefined
} from '@/utils/checks'

import { PrimaryFocus } from '@/domain/primaryFocus/constant'
import { ProviderType } from '@/types/models/Center.model'
import { SponsorTierModule, SPONSOR_TIER } from './sponsorTier'

export const PREMIUM = 'Premium'
export const STANDARD = 'Standard'
export const PICK_THREE = 'PICK_THREE'
export const CENTER_STATUS = {
  CLOSED: 'Closed',
  PUBLISHED: 'Published',
  DRAFT: 'Draft',
  HIDDEN: 'Hidden'
}

export const SPONSOR_BOOST = {
  [SPONSOR_TIER.GLOBAL]: 55000,
  [SPONSOR_TIER.REGIONAL]: 25000,
  [SPONSOR_TIER.LOCAL]: 23000
} as const satisfies Record<number, number>

export const SITE = {
  LUX: 'lux',
  RP: 'rp',
  RPUK: 'rpuk',
  SAMHSA: 'samhsa'
}

export const getProviderType = (center) => {
  const providerType = center?.providerType || center?.provider_type

  if (isNonEmptyArray(providerType)) {
    return providerType[0]
  }

  if (isNonEmptyString(providerType)) {
    return providerType
  }

  return ''
}

export const isPremium = (center) => {
  const cardSize = center?.card_size || center?.cardSize
  return cardSize?.toLowerCase() === PREMIUM.toLowerCase()
}

export const isStandard = (center) => !isPremium(center)
export const isPremiumListing = (center) => center?.listing_template === PREMIUM
export const isFreeListing = (center) =>
  isObject(center) &&
  (isUndefined(center.listing_template) || center.listing_template === STANDARD)

// TODO: check sponsor_tier type
export const isSponsor = (center: { sponsor_tier: number | null }) =>
  SponsorTierModule.isSponsor(center?.sponsor_tier)

// TODO: check sponsor_tier type
export const isPPV = (center: { sponsor_tier: number | null }) =>
  SponsorTierModule.isPPV(center?.sponsor_tier)

// TODO: check sponsor_tier type
export const isCapped = (center: { sponsor_tier: number | null }) => {
  if (!isObject(center)) return false
  return SponsorTierModule.isCapped(center?.sponsor_tier)
}

// TODO: check sponsor_tier type
export const isPaused = (center: { sponsor_tier: number | null }) => {
  if (!isObject(center)) return false
  return SponsorTierModule.isPaused(center?.sponsor_tier)
}

export const isVerified = (center) => center?.certifications?.length > 0

export const isPickThree = (center) => center?.title === PICK_THREE

export const filterOutPickThree = (filterOutPickThree) => (center) =>
  !filterOutPickThree || center?.title !== PICK_THREE

export const isCenter = (maybeCenter) =>
  !isNil(maybeCenter) && !isNil(maybeCenter.title) && maybeCenter.title !== ''

export const isNotClosed = (center) => center?.status !== CENTER_STATUS.CLOSED

export const isCenterAmerican = (centerLocation) =>
  !!centerLocation && centerLocation.includes('United States')

export const sortSpecializationChips = (specializations, highlightedTerms) => {
  if (!isNonEmptyArray(specializations) || !isNonEmptyArray(highlightedTerms)) {
    return specializations || []
  }

  const termsToHighlight =
    isNonEmptyArray(highlightedTerms) && highlightedTerms.some(Array.isArray)
      ? highlightedTerms.flat()
      : highlightedTerms

  return specializations.filter(Boolean).sort((a, b) => {
    if (termsToHighlight?.includes(a)) {
      return -1
    }
    if (termsToHighlight?.includes(b)) {
      return 1
    }
    return 0
  })
}

export function getThirdPartyReviews(center) {
  // tp_review comes from algolia
  // _third_party_reviews comes from the postgres db
  return center?._third_party_reviews || center?.tp_review || []
}

export const getPhoneNumbers = (center) => {
  if (isObject(center) && isNonEmptyArray(center.phone_numbers)) {
    return center?.phone_numbers
  }

  return []
}

export const getWebsite = (center) =>
  isNonEmptyString(center?.website) ? center.website : ''

export const hasWebsite = (center) => isNonEmptyString(getWebsite(center))

export const getSite = (center) => {
  if (!isObject(center)) return []

  return isNonEmptyArray(center.site)
    ? center.site.filter(isNonEmptyString).map((site) => site.toLowerCase())
    : []
}

export const isLuxCenter = (center) => getSite(center).includes(SITE.LUX)

export const isSamhsa = (center) => {
  const sites = getSite(center)
  if (isNil(sites) || isEmptyArray(sites)) {
    return false
  }
  return sites.every((site) => site === SITE.SAMHSA)
}

export const getGeoLoc = (center) => {
  if (isObject(center?._geoloc || center?.geoloc)) {
    return center._geoloc || center.geoloc
  }

  return {}
}

export const getSlug = (center) => {
  return center?.slug || ''
}

export const getCenterProfileSlug = (center) => {
  const slug = getSlug(center)

  if (isLuxCenter(center)) {
    return `/${slug}/`
  }

  return `/p/${center.slug}/`
}

export const getLogo = (center) => {
  if (isNonEmptyString(center?.logo)) {
    return center.logo
  }

  return ''
}

// Depending on how we get the center data, the insurance info can be in different places
export const getAcceptedInsurance = (center) => {
  if (isNonEmptyArray(center?._insurance_info)) {
    return center._insurance_info.filter(isObject)
  }

  if (isNonEmptyArray(center?._accepted_insurance)) {
    return center._accepted_insurance.filter(isObject)
  }

  return []
}

export const getAcceptedInsuranceTitles = (center) => {
  if (isNonEmptyArray(center?.accepted_insurance)) {
    return center?.accepted_insurance.filter(isString)
  }

  return getAcceptedInsurance(center).map((insurance) => insurance.title)
}

export const getLocations = (center) => {
  return (center?._locations || center?.new_locations || []).filter(isObject)
}

export function getReviewsByProviderAndPlatform({
  center,
  provider,
  platform
}) {
  const reviews = getThirdPartyReviews(center).filter(
    (review) =>
      review.provider?.toLowerCase() === provider &&
      review.platform?.toLowerCase() === platform
  )
  return reviews?.length > 0 ? reviews[0] : {}
}

export const getGoogleReviews = (center) => {
  return getThirdPartyReviews(center).filter(
    (review) => review.platform?.toLowerCase() === 'google'
  )
}

export const getGooglePlaceId = (center) => {
  return center?.googlePlaceID || ''
}

export const hasGoogleReviews = (center) =>
  isNonEmptyArray(getGoogleReviews(center))

export function computeThirdPartyReviewsCount(center) {
  return getThirdPartyReviews(center)
    .filter(({ review_count }) => isNumber(review_count))
    .reduce((acc, review) => {
      return acc + review.review_count
    }, 0)
}

export const getTitle = (center) => {
  return center?.title || ''
}

export const getFeaturedImage = (center) => {
  return center?.featuredImage || center?.featured_image
}

export const isCenterEatingDisorders = (center) => {
  return center?.primaryFocus === PrimaryFocus.EATING_DISORDERS
}

export const isCenterSoberLiving = (center) => {
  return center?.providerType?.includes(ProviderType.SOBER_LIVING)
}

// There is typo on the DB, so we need to check for both
export const getAccreditation = (center) => {
  if (isNonEmptyString(center?.accredidation || center?.accreditation)) {
    const value = center.accredidation || center.accreditation
    return value === 'None' ? '' : value
  }

  return ''
}

export const getLocationLabel = (center) =>
  center?.locationLabel || center?.location_label || ''

export const getAddress = (center) => center?.address || ''

/**
 * Checks if a center has certifications
 * @param {Object} center - The center object
 * @returns {boolean} True if the center has certifications
 */
export const centerHasCertifications = (center) =>
  isNonEmptyArray(center?.certifications)

/**
 * Checks if a center is claimed
 * @param {Object} center - The center object
 * @returns {boolean} True if the center has a claimed email
 */
export const centerIsClaimed = (center) =>
  isNonEmptyString(center?.claimed_email)

/**
 * Checks if a center has no pricing information
 * @param {Object} center - The center object
 * @returns {boolean} True if the center has no pricing
 */
export const centerHasNoPricing = (center) => !isNonEmptyArray(center?.price)

/**
 * Checks if a center is both claimed and verified
 * @param {Object} center - The center object
 * @returns {boolean} True if the center is claimed and has certifications
 */
export const centerIsClaimedAndVerified = (center) =>
  centerIsClaimed(center) && centerHasCertifications(center)

/**
 * Checks if a center is in a specific location
 * @param {Object} center - The center object with locations array
 * @param {string} location - The location to check
 * @returns {boolean} True if the center is in the specified location
 */
export const isCenterInLocation = (center = { locations: [] }, location = '') =>
  center?.locations?.includes(location) ||
  center?.locations?.some((loc) => loc?.title === location)

/**
 * Checks if a center is in the United States
 * @param {Object} center - The center object
 * @returns {boolean} True if the center is in the United States
 */
export const isCenterInUnitedStates = (center = { locations: [] }) =>
  isCenterInLocation(center, 'United States')

/**
 * Gets the appropriate address to display on a center card
 * @param {Object} center - The center object
 * @returns {string} The address to display on the card
 */
export const getCenterAddressForCard = (center) => {
  if (isPremium(center)) {
    return getLocationLabel(center)
  }

  const centerAddress = getAddress(center)

  if (isNonEmptyString(centerAddress) && isCenterInUnitedStates(center)) {
    return centerAddress
  }
  return getLocationLabel(center)
}

/**
 * Gets the carousel image URL for a center
 * @param {Object} center - The center object
 * @returns {string} The URL for the center's carousel image
 */
export const getCenterCarouselImageUrl = (center) => {
  const baseUrl =
    'https://res.cloudinary.com/rehabpath/image/upload/e_gen_restore/c_fill,q_15,w_640,h_400/f_auto/'
  return center.featured_image
    ? `${baseUrl}${center.featured_image.split('/').pop()}`
    : `${baseUrl}${center.slug}-thumb`
}

export const getAdmissionsCount = (center) => {
  if (!isObject(center)) {
    return 0
  }
  return center?.rdcAdmits || center?.rdc_admits || 0
}

export const getEmail = (center) => center?.email || ''
export const hasEmail = (center) => isNonEmptyString(getEmail(center))

/**
 * Gets the center ID
 * @param {Object} center - The center object
 * @returns {string} The center ID (pid or id)
 */
export const getCenterId = (center) => center?.pid || center?.id
